import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Steps } from 'antd';

import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import AttestationModal from '../users/AttestationModal';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { anchorOf } from '../../platform';
import useEnsureCards from '../../hooks/useEnsureCards';
import useWindowClass from '../../hooks/useWindowClass';
import { downloadBlob } from '../../services/exports';
import { getDateFormat } from '../../utils/dateDisplay';
import { preferredDateOrder } from '../../utils/spreadsheet/cellValues';
import { importableElements, suggestMapping, tabulate } from '../../utils/spreadsheet/columns';
import { sheetProblem, readSpreadsheet } from '../../utils/spreadsheet/readFile';
import { mappedColumns } from '../../utils/spreadsheet/rows';
import { rowsToFixCsv, rowsToFixName } from '../../utils/spreadsheet/rowsToFix';
import ChooseFileStep from './ChooseFileStep';
import CheckStep from './CheckStep';
import ImportResultStep from './ImportResultStep';
import MatchColumnsStep from './MatchColumnsStep';
import { STEP_TITLES, checkSummary, countWords, failureLines, unsentLine } from './importCopy';
import useImportRun from './useImportRun';

const FILE = 0;
const MAP = 1;
const CHECK = 2;
const IMPORT = 3;

const EMPTY = Object.freeze({});

/**
 * THE SPREADSHEET IMPORTER (KPR-1899), written for every card.
 *
 * Choose a file, match its columns to the card's elements, read the dry run,
 * import. The file is read here and never uploaded; only the mapped values
 * leave, row by row. Back never discards: the file and the mapping stay until
 * the person changes them.
 *
 * It knows nothing of where rows go. `send(body, config)` takes one call of the
 * ingest body and answers as ingest does: the page passes POST …/ingest, and a
 * bundle's Bring your data step (KPR-1900, docs/plans/bundle-setup.md) passes
 * its own …/setup/steps/{step}/rows.
 *
 *   collection, collectionId   where the rows land
 *   card                       the schema card (GET …/schema), null until chosen
 *   cardPicker                 the page's card field for step 1, if it has one
 *   canUndo                    the person manages the collection
 *   zone                       the collection's time zone
 */
export default function SpreadsheetImporter({ collectionId, card, cardPicker = null, send, zone = null, canUndo = false }) {
	const navigate = useNavigate();
	const { compact } = useWindowClass();
	const { cardsById, getElementsForCard } = useCardDefinitions();
	const [step, setStep] = useState(FILE);
	const [book, setBook] = useState(null);
	const [sheetIndex, setSheetIndex] = useState(0);
	const [headerAt, setHeaderAt] = useState(null);
	const [reading, setReading] = useState(false);
	const [fileError, setFileError] = useState(null);
	const [mapping, setMapping] = useState(EMPTY);
	const [signing, setSigning] = useState(null);

	const requestSignature = useCallback(count => new Promise(resolve => {
		setSigning({ count, resolve });
	}), []);

	const run = useImportRun({ collectionId, card, send, zone, cardsById, getElementsForCard, requestSignature });
	const { check, commit, runCheck, prepareCommit, runCommit, stopCommit, resetRun } = run;

	const sheet = useMemo(() => {
		const read = book?.sheets[sheetIndex] || null;
		return read && headerAt !== null ? { ...read, ...tabulate(read.raw, headerAt) } : read;
	}, [book, sheetIndex, headerAt]);
	const dateDefault = preferredDateOrder(getDateFormat());
	const { offered, withheld } = useMemo(() => importableElements(card), [card]);
	const elementsByName = useMemo(() => Object.fromEntries(offered.map(el => [el.name, el])), [offered]);
	const suggestions = useMemo(() => (sheet ? suggestMapping(
		sheet.columns.filter(column => !mapping[column.index]?.left),
		offered,
		mapping,
	) : EMPTY), [sheet, offered, mapping]);

	const lookupCards = useMemo(() => offered.filter(el => el.dataType === 'card-lookup' && el.lookupCardId).map(el => el.lookupCardId), [offered]);
	useEnsureCards(lookupCards);

	const finished = commit.phase === 'done' && !commit.result?.unsent?.length;
	const { resetDirty } = useDirtyForm({ file: book?.fileName || null, mapping }, { enabled: !finished });

	// Another card has other elements, so a mapping cannot carry over.
	const cardId = card?.id || null;
	const firstCard = useRef(cardId);
	useEffect(() => {
		if (firstCard.current === cardId) return;
		firstCard.current = cardId;
		setMapping(EMPTY);
		resetRun();
	}, [cardId, resetRun]);

	const onFile = useCallback(async file => {
		setReading(true);
		setFileError(null);
		resetRun();
		try {
			const next = await readSpreadsheet(file);
			setBook(next);
			setSheetIndex(0);
			setHeaderAt(null);
			setMapping(EMPTY);
			if (next.sheets.length === 1 && !sheetProblem(next.sheets[0])) setStep(MAP);
		} catch (e) {
			setBook(null);
			setFileError(e?.name === 'ImportFileError' ? e.message : 'keepr could not read this file. Choose it again.');
		} finally {
			setReading(false);
		}
	}, [resetRun]);

	const onSheet = useCallback(index => {
		setSheetIndex(index);
		setHeaderAt(null);
		setMapping(EMPTY);
		resetRun();
	}, [resetRun]);

	const onHeader = useCallback(rowNumber => {
		setHeaderAt(rowNumber - 1);
		setMapping(EMPTY);
		resetRun();
	}, [resetRun]);

	const onMap = useCallback((index, entry) => setMapping(prev => ({ ...prev, [index]: entry })), []);
	const onAccept = useCallback(index => {
		const suggestion = suggestions[index];
		if (suggestion) setMapping(prev => ({ ...prev, [index]: suggestion }));
	}, [suggestions]);
	const onAcceptAll = useCallback(() => setMapping(prev => ({ ...prev, ...suggestions })), [suggestions]);

	const startCheck = useCallback(() => {
		setStep(CHECK);
		runCheck({ sheet, mapping, elements: offered, fileName: book.fileName, dateDefault });
	}, [book, dateDefault, mapping, offered, runCheck, sheet]);

	const startCommit = useCallback(async () => {
		if (!await prepareCommit()) return;
		setStep(IMPORT);
		runCommit();
	}, [prepareCommit, runCommit]);

	const startOver = useCallback(() => {
		resetRun();
		setBook(null);
		setHeaderAt(null);
		setMapping(EMPTY);
		setFileError(null);
		setStep(FILE);
	}, [resetRun]);

	const mapped = useMemo(() => (sheet ? mappedColumns(sheet.columns, mapping, offered) : []), [sheet, mapping, offered]);
	const context = useMemo(() => ({
		sheet,
		elementsByName,
		columnsByElement: Object.fromEntries(mapped.map(({ column, element }) => [element.name, column])),
	}), [sheet, elementsByName, mapped]);

	const toLines = useCallback(entries => (sheet ? entries.flatMap(entry => failureLines(entry, context)) : []), [context, sheet]);
	const checkLines = useMemo(() => toLines(check.result?.failed || []), [check.result, toLines]);
	const commitLines = useMemo(() => toLines(commit.result?.failed || []), [commit.result, toLines]);
	const allLines = useMemo(
		() => [...checkLines, ...commitLines, ...(commit.result?.unsent || []).map(unsentLine)],
		[checkLines, commitLines, commit.result]
	);

	const downloadRows = useCallback(event => downloadBlob(
		new Blob([rowsToFixCsv({ sheet, lines: allLines })], { type: 'text/csv;charset=utf-8' }),
		rowsToFixName(book?.fileName),
		{ anchor: anchorOf(event) },
	), [allLines, book, sheet]);

	const ready = check.result?.ready.length || 0;
	const failedRows = check.result?.failed.length || 0;
	const unsent = commit.result?.unsent?.length || 0;
	const running = commit.phase === 'running';

	let actions = null;
	if (step === FILE) {
		actions = book && sheet && !sheetProblem(sheet) && card
			? <Button type="primary" onClick={() => setStep(MAP)}>Match columns</Button>
			: null;
	} else if (step === MAP) {
		actions = (
			<>
				<Button onClick={() => setStep(FILE)}>Back</Button>
				<Button type="primary" disabled={!mapped.length} onClick={startCheck}>Check rows</Button>
			</>
		);
	} else if (step === CHECK) {
		actions = (
			<>
				<Button onClick={() => setStep(MAP)}>Back</Button>
				{failedRows ? <Button onClick={downloadRows} disabled={check.phase !== 'done'}>Download rows to fix</Button> : null}
				<Button type="primary" disabled={check.phase !== 'done' || !ready} onClick={startCommit}>
					{ready ? `Import ${countWords(ready, 'row')}` : 'Import'}
				</Button>
			</>
		);
	} else if (running) {
		actions = <Button loading={Boolean(commit.stopping)} onClick={stopCommit}>Stop</Button>;
	} else {
		actions = (
			<>
				{allLines.length ? <Button onClick={downloadRows}>Download rows to fix</Button> : null}
				<Button onClick={startOver}>Import another file</Button>
				{unsent
					? <Button type="primary" onClick={runCommit}>{`Send the other ${countWords(unsent, 'row')}`}</Button>
					: (
						<Button
							type="primary"
							onClick={() => {
								resetDirty();
								navigate(`/collections/${collectionId}/items`);
							}}
						>
							Go to the items
						</Button>
					)}
			</>
		);
	}

	const settleSignature = token => {
		const resolve = signing?.resolve;
		setSigning(null);
		resolve?.(token);
	};

	const stepWords = `Step ${step + 1} of ${STEP_TITLES.length} · ${STEP_TITLES[step]}`;

	return (
		<div className="spreadsheet-import">
			{compact
				? <p className="spreadsheet-import__step-line">{stepWords}</p>
				: <Steps size="small" className="spreadsheet-import__steps" current={step} status={finished ? 'finish' : 'process'} items={STEP_TITLES.map(title => ({ title }))} />}
			<p className="spreadsheet-import__live" aria-live="polite">{stepWords}</p>
			{step === FILE ? (
				<ChooseFileStep
					cardPicker={cardPicker}
					card={card}
					book={book}
					sheetIndex={sheetIndex}
					reading={reading}
					error={fileError}
					onFile={onFile}
					onSheet={onSheet}
				/>
			) : null}
			{step === MAP ? (
				<MatchColumnsStep
					sheet={sheet}
					elements={offered}
					withheld={withheld}
					mapping={mapping}
					suggestions={suggestions}
					dateDefault={dateDefault}
					onHeader={onHeader}
					onMap={onMap}
					onAccept={onAccept}
					onAcceptAll={onAcceptAll}
				/>
			) : null}
			{step === CHECK ? (
				<CheckStep
					check={check}
					summary={check.result ? checkSummary({ ready, failed: failedRows, empty: check.result.empty }) : ''}
					lines={checkLines}
					onRetry={startCheck}
				/>
			) : null}
			{step === IMPORT ? (
				<ImportResultStep
					commit={commit}
					cardName={card?.name || 'this card'}
					lines={commitLines}
					leftAtCheck={failedRows}
					imports={`/collections/${collectionId}/form?tab=imports`}
					canUndo={canUndo}
				/>
			) : null}
			{actions ? <div className="spreadsheet-import__foot">{actions}</div> : null}
			<AttestationModal
				open={Boolean(signing)}
				mode={card?.record?.attest?.mode === 'password' ? 'password' : 'pin'}
				collectionId={collectionId}
				cardId={card?.id}
				count={signing?.count || 1}
				title="Import"
				onClose={() => settleSignature(null)}
				onSigned={settleSignature}
			/>
		</div>
	);
}
